<template>
  <c-modal :hideCloseBtn="true" :persistent="true" :value="true">
    <div
      class="d-flex flex-column align-center"
      v-if="!shouldShowImtokenNotice && !shouldShowMetamaskNotice"
    >
      <div class="heading-4 font-weight-medium text-center">
        {{ txHash ? title : "Processing transaction" }}
      </div>
      <div class="mt-5 darkgray--text line-height-25 text-4 text-center">
        {{ txHash ? description : "Please be patient." }}
      </div>
      <v-progress-circular
        indeterminate
        :size="80"
        :width="6"
        color="primary"
        class="mt-10"
      ></v-progress-circular>
      <div class="mt-10 text-4" v-if="!!txHash">
        <a
          :href="project.config.explorerTxsLink + txHash"
          target="_blank"
          rel="noopener noreferrer"
          class="text-decoration-none outline-none"
          >Click to view status of transaction</a
        >
      </div>
    </div>
    <imtoken-notice
      v-if="shouldShowImtokenNotice"
      class="py-3"
      context="transaction"
      @next="$emit('cancel')"
    />
    <metamask-notice
      v-if="shouldShowMetamaskNotice"
      class="py-3"
      context="transaction"
      @next="$emit('cancel')"
    />
  </c-modal>
</template>

<script>
import { mapGetters } from "vuex";
import Config from "@/common/config";
import AppConst from "@/common/constants";
import ImtokenNotice from "@/components/wallet/WalletNotice/ImToken";
import MetamaskNotice from "@/components/wallet/WalletNotice/Metamask";

const {
  fortmatic: fortmaticOptions,
  torus: TorusConfig,
  walletconnect,
} = Config;
const TX_TIMEOUT = 120000;

export default {
  props: {
    txObject: Object,
    txType: String,
    vault: String,
    vaultToken: String,
    metadata: Object,
    title: {
      type: String,
      default: "Processing transaction",
    },
    description: {
      type: String,
      default: "Please be patient.",
    },
  },
  components: {
    ImtokenNotice,
    MetamaskNotice,
  },
  data: () => ({
    shouldShowImtokenNotice: false,
    shouldShowMetamaskNotice: false,
    txHash: null,
  }),
  computed: {
    ...mapGetters({
      activeBackend: "walletUtils/getActiveBackend",
      validBackend: "walletUtils/getValidBackend",
      signableWallet: "userWallets/signableWallet",
      isImToken: "appConfig/isImToken",
      isMetamask: "appConfig/isMetamask",
      project: "projectConfig/latestProject",
    }),
    accountHdPath() {
      const { hdPath } = this.signableWallet.metadata || {};
      return hdPath || "";
    },
  },
  methods: {
    async connectWallet() {
      this.$store.dispatch("walletUtils/setBackend", this.signableWallet.type);
      let isAlive;
      if (this.signableWallet.type === "walletconnect") {
        isAlive = await this.isWalletConnectSessionAlive();
      }
      try {
        await this.$store.dispatch("walletUtils/importBackend", {
          fortmaticOptions,
          coinName: this.signableWallet.coin_network,
          torusOptions: { network: TorusConfig.network },
          walletconnect: {
            projectId: walletconnect.projectId,
            network: "ethereum",
            accountAddress: this.signableWallet.address,
          },
        });
        if (this.validBackend && this.validBackend !== true) {
          throw new Error(this.validBackend);
        }
        const account = await this.activeBackend.getAccount({
          hdPath: this.accountHdPath,
        });
        const { address: accountAddress } = await account.getAddress();
        if (
          accountAddress.toLowerCase() !==
          this.signableWallet.address.toLowerCase()
        ) {
          throw new Error(
            `Please use the correct account to sign the transaction.\nExpected address: ${this.signableWallet.address}.\nActual address: ${accountAddress}.`
          );
        }
        return account;
      } catch (error) {
        if ("function" === typeof this.activeBackend?.cleanUp) {
          this.activeBackend.cleanUp();
        }
        this.$emit("onError", error);
      }
    },
    async confirm() {
      if (this.signableWallet.type === "imtoken" && !this.isImToken) {
        return (this.shouldShowImtokenNotice = true);
      }
      if (this.signableWallet.type === "metamask" && !this.isMetamask) {
        return (this.shouldShowMetamaskNotice = true);
      }
      this.processing = true;
      let errorCode = AppConst.TX_ERROR.CONNECT_TIMEOUT;
      setTimeout(() => {
        if (this.processing) {
          this.$emit(
            "onError",
            new Error(
              `Transaction timeout. Please try again.\n(Error code: ${errorCode})`
            )
          );
          this.processing = false;
        }
      }, TX_TIMEOUT);
      const account = await this.connectWallet();
      if (account) {
        try {
          errorCode = AppConst.TX_ERROR.SIGN_TIMEOUT;
          const { txHash, signedTx } = await account.signTransaction(
            this.txObject
          );
          errorCode = AppConst.TX_ERROR.BROADCAST_TIMEOUT;
          await this.broadcastTx({
            signedTx: signedTx?.slice(2) ?? undefined,
            txHash,
            type: this.txType,
            metadata: this.metadata,
            vault: this.vault,
            token: this.vaultToken,
          });
          this.txHash = txHash;
          this.$emit("onSuccess", txHash);
        } catch (error) {
          this.$emit("onError", error);
        }
      }
      this.processing = false;
    },
    async broadcastTx(payload) {
      return this.$store.dispatch("vaults/broadcastTx", payload);
    },
    async isWalletConnectSessionAlive() {
      try {
        const walletconnect = JSON.parse(localStorage.getItem("walletconnect"));
        const topic = walletconnect?.peerId;
        if (!topic) return false;
        const { isAlive } = await fetch(
          `${walletConnectBridge}/checkStatus/${topic}`
        ).then(r => r.json());
        return isAlive;
      } catch (error) {
        return false;
      }
    },
  },
  mounted() {
    this.confirm();
  },
};
</script>
